import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useBranding, resolveLogo } from "@/contexts/BrandingContext";
import TopToolbar from "@/components/TopToolbar";
import { Navigate } from "react-router-dom";

export default function LoginPage() {
  const { user, login } = useAuth();
  const { t, theme, dir, lang } = useI18n();
  const { branding } = useBranding();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Recruiter accounts skip the employee dashboard entirely.
  if (user) {
    const code = ((user.role || {}).code || user.role_code || "").toLowerCase();
    const dest = code === "ats_recruiter" ? "/ats/dashboard" : "/";
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(username.trim(), password);
    setBusy(false);
    if (!res.ok) setError(res.error);
  };

  const styles = makeStyles(theme);

  return (
    <div dir={dir} style={styles.page} data-testid="login-page">
      <TopToolbar />
      <div style={styles.card}>
        <img src={resolveLogo(branding.logo_url)} alt={branding.app_name_ar} style={styles.logo} data-testid="login-logo" />
        <h1 style={styles.title}>{lang === "ar" ? branding.app_name_ar : branding.app_name_en}</h1>
        <p style={styles.subtitle}>{t("login.subtitle")}</p>

        <form onSubmit={submit} style={styles.form}>
          <label style={styles.label} htmlFor="username">{t("login.username")}</label>
          <input
            id="username"
            data-testid="login-username-input"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={styles.input}
            placeholder={t("login.username_placeholder")}
            required
            autoComplete="username"
          />
          <label style={styles.label} htmlFor="password">{t("login.password")}</label>
          <div style={styles.passwordWrap}>
            <input
              id="password"
              data-testid="login-password-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ ...styles.input, paddingInlineEnd: 46, width: "100%", boxSizing: "border-box" }}
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              data-testid="login-password-toggle"
              aria-label={
                showPassword
                  ? (lang === "ar" ? "إخفاء كلمة المرور" : "Hide password")
                  : (lang === "ar" ? "إظهار كلمة المرور" : "Show password")
              }
              title={
                showPassword
                  ? (lang === "ar" ? "إخفاء" : "Hide")
                  : (lang === "ar" ? "إظهار" : "Show")
              }
              style={styles.eyeBtn(dir)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {error && (
            <div style={styles.error} data-testid="login-error">{error}</div>
          )}
          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={busy}
            style={{ ...styles.submit, opacity: busy ? 0.6 : 1 }}
          >
            {busy ? t("login.submitting") : t("login.submit")}
          </button>
        </form>

        <p style={styles.note}>{t("office.name")}</p>
      </div>
    </div>
  );
}

const makeStyles = (theme) => ({
  page: {
    minHeight: "100vh",
    background: theme.bgGradient,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    fontFamily: "'Tajawal','Segoe UI', sans-serif",
    transition: "background 200ms ease",
  },
  card: {
    width: "100%",
    maxWidth: "420px",
    background: theme.name === "dark" ? "rgba(20,20,20,0.85)" : "rgba(255,255,255,0.92)",
    border: `1px solid ${theme.borderAccent}`,
    borderRadius: "16px",
    padding: "32px 28px",
    backdropFilter: "blur(16px)",
    boxShadow: theme.cardShadow,
    color: theme.text,
  },
  logo: {
    display: "block",
    width: "140px",
    height: "140px",
    objectFit: "contain",
    margin: "0 auto 8px",
  },
  title: { fontSize: "20px", fontWeight: 700, margin: "8px 0 4px", textAlign: "center", color: theme.text },
  subtitle: { fontSize: "13px", color: theme.textMuted, textAlign: "center", marginBottom: "28px" },
  form: { display: "flex", flexDirection: "column", gap: "8px" },
  label: { fontSize: "13px", color: theme.textSoft, marginTop: "8px" },
  input: {
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: "10px",
    padding: "12px 14px",
    color: theme.text,
    fontSize: "15px",
    outline: "none",
  },
  passwordWrap: {
    position: "relative",
    display: "flex",
    alignItems: "stretch",
  },
  eyeBtn: (dir) => ({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    [dir === "rtl" ? "left" : "right"]: 8,
    width: 36,
    height: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    color: theme.textMuted,
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    padding: 0,
  }),
  error: {
    background: theme.errorBg,
    color: theme.errorText,
    border: `1px solid ${theme.errorBorder}`,
    padding: "10px 12px",
    borderRadius: "8px",
    fontSize: "13px",
    marginTop: "8px",
  },
  submit: {
    marginTop: "16px",
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#ffffff",
    fontWeight: 700,
    border: "none",
    borderRadius: "10px",
    padding: "14px",
    fontSize: "16px",
    cursor: "pointer",
  },
  note: {
    marginTop: "20px",
    fontSize: "11px",
    color: theme.textFaint,
    textAlign: "center",
    letterSpacing: "1px",
  },
});
